import React from "react";
import { SimpleTableComponent } from "../tables/simpleTable/SimpleTableComponent";
import { SimpleTableRow } from "../tables/simpleTable/SimpleTableRow";
import { SimpleTableHeader } from "../tables/simpleTable/SimpleTableHeader";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { deleteCompositeAsset, getCompositeAssets } from "../../api/api";
import { DesignationType } from "common/types/comparableFields.types";
import { SimpleTableElement } from "../tables/simpleTable/SimpleTableElement";
import { formatArea, formatPrice } from "common/helpers/text.helpers";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Alert, IconButton, useMediaQuery } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ConfirmationDialog } from "../dialogs/ConfirmationDialog";
import { CompositeAsset } from "../../types/api.types";
import { ConservativeValue } from "common/types/common.types";
import { showToastError } from "common/toast/toast";
import { getErrorMessage } from "common/helpers/error.helpers";
import {
  CompositeAssetsForm,
  getInitialCompositeAssetWithPurchasedArea,
} from "./CompositeAssetsForm";
import { theme } from "../../styles/MUITheme";
import { useTranslation } from "react-i18next";
import { getDesignationTypeTranslation } from "../../helpers/comparableFields.helpers";

interface CompositeAssetsTableProps {
  propertyId: string;
  pricePredicitionId?: string;
  isCustomer?: boolean;
  readonly?: boolean;
  compositeAssets?: CompositeAsset[];
  hideValue?: boolean;
  conservativeValue?: ConservativeValue;
  shouldCheckForUniqueNr?: boolean;
}

export const CompositeAssetsTable: React.FC<CompositeAssetsTableProps> = ({
  propertyId,
  isCustomer,
  pricePredicitionId,
  readonly,
  compositeAssets,
  hideValue,
  conservativeValue,
  shouldCheckForUniqueNr,
}) => {
  const { t } = useTranslation();
  const { data: fetchedData } = useQuery(
    ["compositeAssets", propertyId, pricePredicitionId],
    () => getCompositeAssets(propertyId, pricePredicitionId),
    {
      enabled: !compositeAssets,
    }
  );

  const data = compositeAssets ?? fetchedData;

  const assetsMissingUniqueNr = shouldCheckForUniqueNr
    ? data?.filter((asset) => !asset.uniqueNr?.trim()) ?? []
    : [];

  const queryClient = useQueryClient();

  const { mutate: mutateDelete, isLoading: isDeleting } = useMutation(
    (id: string) => deleteCompositeAsset(id, propertyId),
    {
      onSuccess: () => {
        setIsDeleteOpen(false);
        queryClient.invalidateQueries("compositeAssets");
        queryClient.invalidateQueries("report_price");
      },
      onError: (error) => {
        showToastError(getErrorMessage(error));
      },
    }
  );

  const totalSum = data?.reduce((acc, curr) => acc + (curr.value ?? 0), 0);

  const showValue = !isCustomer && !hideValue;
  const showConservativeValue = showValue && !!conservativeValue;

  const getConservativeValue = (asset: CompositeAsset) => {
    if (!conservativeValue) {
      return undefined;
    }
    if (asset._id === "main") {
      return conservativeValue.main_asset?.conservative_value;
    }
    return conservativeValue.composite_assets?.find(
      (compositeAsset) => compositeAsset.id === asset._id
    )?.conservative_value;
  };

  const [selectedDeleteAsset, setSelectedDeleteAsset] =
    React.useState<CompositeAsset>();

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

  const [selectedEditAsset, setSelectedEditAsset] =
    React.useState<CompositeAsset>();

  const [isEditOpen, setIsEditOpen] = React.useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) && !readonly;

  return (
    <>
      {assetsMissingUniqueNr.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 20 }}>
          {t("propertyDetails.compositeAssets.missingUniqueNrWarning", {
            count: assetsMissingUniqueNr.length,
          })}
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            {assetsMissingUniqueNr.map((asset) => (
              <li key={asset._id}>{asset.name}</li>
            ))}
          </ul>
        </Alert>
      )}
      <div
        style={{
          overflowX: "auto",
        }}
      >
        <SimpleTableComponent
          customFontSize="14px"
          style={{
            minWidth: "500px",
            overflowX: "scroll",
          }}
        >
          <SimpleTableRow>
            <SimpleTableHeader isFirstElement>
              {t("propertyDetails.compositeAssets.table.name")}
            </SimpleTableHeader>
            {!isMobile && (
              <SimpleTableHeader textAlign="left">
                {t("propertyDetails.compositeAssets.table.type")}
              </SimpleTableHeader>
            )}
            {!isMobile && (
              <SimpleTableHeader textAlign="left">
                {t("propertyDetails.compositeAssets.table.uniqueNumber")}
              </SimpleTableHeader>
            )}
            {!isMobile && (
              <SimpleTableHeader textAlign="left">
                {t("propertyDetails.compositeAssets.table.address")}
              </SimpleTableHeader>
            )}
            <SimpleTableHeader textAlign="right">
              {t("propertyDetails.compositeAssets.table.purchasedArea")}
            </SimpleTableHeader>
            <SimpleTableHeader textAlign="right">
              {t("propertyDetails.compositeAssets.table.valuedPart")}
            </SimpleTableHeader>

            {showValue && (
              <SimpleTableHeader textAlign="right">
                {t("propertyDetails.compositeAssets.table.marketPrice")}
              </SimpleTableHeader>
            )}
            {showConservativeValue && (
              <SimpleTableHeader textAlign="right">
                {t("propertyDetails.compositeAssets.table.conservativeValue")}
                <sup>1</sup>
              </SimpleTableHeader>
            )}
            {!readonly && <SimpleTableHeader />}
          </SimpleTableRow>
          {data?.map((compositeAsset) => {
            const fixedCompositeAsset =
              getInitialCompositeAssetWithPurchasedArea(compositeAsset);
            return (
              <SimpleTableRow key={compositeAsset._id}>
                <SimpleTableElement isFirstElement>
                  {compositeAsset.name}
                </SimpleTableElement>
                {!isMobile && (
                  <SimpleTableElement textAlign="left">
                    {getDesignationTypeTranslation(
                      compositeAsset.designationTypeId as DesignationType
                    )}
                  </SimpleTableElement>
                )}
                {!isMobile && (
                  <SimpleTableElement textAlign="left">
                    {compositeAsset.uniqueNr}
                  </SimpleTableElement>
                )}
                {!isMobile && (
                  <SimpleTableElement textAlign="left">
                    {compositeAsset.address}
                  </SimpleTableElement>
                )}
                <SimpleTableElement>
                  {`${fixedCompositeAsset.areaPurchased} ${compositeAsset.areaUnit}`}
                </SimpleTableElement>

                <SimpleTableElement>
                  {compositeAsset.assetPart1}/{compositeAsset.assetPart2}
                </SimpleTableElement>

                {showValue && (
                  <SimpleTableElement>
                    {formatPrice(compositeAsset.value)}
                  </SimpleTableElement>
                )}
                {showConservativeValue && (
                  <SimpleTableElement>
                    {formatPrice(getConservativeValue(compositeAsset))}
                  </SimpleTableElement>
                )}
                {!readonly && (
                  <SimpleTableElement>
                    {compositeAsset._id !== "main" && (
                      <>
                        <IconButton
                          color="inherit"
                          onClick={() => {
                            setSelectedDeleteAsset(compositeAsset);
                            setIsDeleteOpen(true);
                          }}
                          disabled={
                            (isCustomer && !compositeAsset.isCustomer) ||
                            !!compositeAsset.isPlotArea
                          }
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          onClick={() => {
                            setSelectedEditAsset(compositeAsset);
                            setIsEditOpen(true);
                          }}
                          disabled={isCustomer && !compositeAsset.isCustomer}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </>
                    )}
                  </SimpleTableElement>
                )}
              </SimpleTableRow>
            );
          })}
          {showValue && (
            <SimpleTableRow>
              <SimpleTableElement isFirstElement />
              {!isMobile && <SimpleTableElement />}
              {!isMobile && <SimpleTableElement />}
              {!isMobile && <SimpleTableElement />}
              <SimpleTableElement />
              <SimpleTableElement>
                <b>{t("propertyDetails.compositeAssets.table.total")}</b>
              </SimpleTableElement>
              <SimpleTableElement>
                <b>{formatPrice(totalSum)}</b>
              </SimpleTableElement>
              {showConservativeValue && (
                <SimpleTableElement>
                  <b>
                    {formatPrice(
                      conservativeValue?.total?.conservative_value
                    )}
                  </b>
                </SimpleTableElement>
              )}
              {!readonly && <SimpleTableElement />}
            </SimpleTableRow>
          )}
        </SimpleTableComponent>
      </div>
      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={() => {
          if (selectedDeleteAsset) {
            mutateDelete(selectedDeleteAsset._id);
          }
        }}
        title={t("propertyDetails.compositeAssets.deleteDialog.title")}
        description={t(
          "propertyDetails.compositeAssets.deleteDialog.description",
          {
            name: selectedDeleteAsset?.name,
          }
        )}
        hideDoNotShowAgain
        actionButtonText={t("common.delete")}
        isLoading={isDeleting}
      />
      {!readonly && (
        <CompositeAssetsForm
          propertyId={propertyId}
          compositeAsset={selectedEditAsset}
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          isCustomer={isCustomer}
          hideValue={hideValue}
        />
      )}
    </>
  );
};
